import React, { useState } from "react";
import { Box, Stack, Typography, Container, Button, Modal, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Eye, X } from "lucide-react";
import testRecommenderImage from "../assets/templates/Addons/recomm page.jpg";
import dietPageImage from "../assets/templates/Addons/diet page.jpg";
import doctorPageImage from "../assets/templates/Addons/doctor page.jpg";
import quickHealthImage from "../assets/templates/Addons/quick health page.jpg";
import wellnezzPageImage from "../assets/templates/Addons/wellnezz page.jpg";

// Unified primary blue for Add-Ons cards (titles + buttons)
const ADD_ON_PRIMARY_COLOR = "#4673D9";

// Fixed image height for all three cards (desktop only)
// Mobile / tablet will size cards based on the natural image height.
const IMAGE_HEIGHT = { md: "340px" };

const AddOnsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  backgroundColor: "transparent",
  padding: "70px 0 60px 0",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    padding: "70px 0 80px 0",
  },
}));

// Modern Add-On Card with proper width
const AddOnCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  flex: "1 1 0",
  background: "linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 50%, #F1F5FF 100%)",
  borderRadius: "18px",
  border: "1px solid rgba(70, 115, 217, 0.08)",
  transition: "all 0.3s ease",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 10px 28px rgba(37, 68, 132, 0.08)",
  padding: "30px",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 14px 32px rgba(37, 68, 132, 0.12)",
  },
  // Desktop: cap each card so lone cards on last row don't stretch full-width
  [theme.breakpoints.up("md")]: {
    maxWidth: "calc(33.333% - 16px)",
  },
  // Mobile/tablet: full width cards stacked vertically
  [theme.breakpoints.down("md")]: {
    width: "100%",
    marginBottom: theme.spacing(3),
    padding: "20px",
    flex: "none",
  },
}));

// White rounded frame container for images - fixed height on desktop
const ImageFrame = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: "16px",
  overflow: "hidden",
  position: "relative",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "10px solid #E1E4E8",
  flexShrink: 0,
  boxShadow: "0 6px 18px rgba(0, 0, 0, 0.06)",
}));


// Common styles for card images
// - Mobile/tablet: image controls the card height, full image visible (no cropping)
// - Desktop: image fills fixed-height frame
const cardImageStyles = {
  width: "100%",
  height: { xs: "auto", md: "100%" },
  objectFit: { xs: "contain", md: "cover" },
  objectPosition: "center top",
  display: "block",
  borderRadius: "10px",
};

// Common styles for card title
const cardTitleStyles = {
  color: ADD_ON_PRIMARY_COLOR,
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "20px", md: "20px" },
  fontWeight: 800,
  letterSpacing: "-0.02em",
  lineHeight: { xs: "28px", md: "28px" },
  mb: 1,
  whiteSpace: "nowrap",
};

// Common styles for card description
const cardDescriptionStyles = {
  color: "#4B5563",
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "14px", md: "15px" },
  fontWeight: 400,
  lineHeight: { xs: "22px", md: "24px" },
  mb: 2,
  flexGrow: 1,
};

// Common styles for preview button
const previewButtonStyles = {
  color: "#FFFFFF",
  fontFamily: "Inter, sans-serif",
  fontSize: { xs: "13px", md: "14px" },
  fontWeight: 600,
  textTransform: "none",
  padding: { xs: "10px 18px", md: "12px 22px" },
  background: ADD_ON_PRIMARY_COLOR,
  borderRadius: "999px",
  border: "none",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  transition: "all 0.3s ease",
  "&:hover": {
    background: ADD_ON_PRIMARY_COLOR,
    opacity: 0.92,
    transform: "translateY(-2px)",
    boxShadow: "0 8px 18px rgba(70, 115, 217, 0.24)",
  },
};

const AddOnsSection = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");

  const handleOpenModal = (image, title) => {
    setSelectedImage(image);
    setSelectedTitle(title);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedImage(null);
    setSelectedTitle("");
  };

  return (
    <AddOnsContainer>
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, px: { xs: 2, md: 3 } }}>
        {/* Heading Section with Animation */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{
            textAlign: "center",
            mb: { xs: 3, md: 4 },
          }}
        >
          <Stack 
            direction="row" 
            justifyContent="center" 
            alignItems="baseline" 
            sx={{ mb: 2 }}
          >
            <Typography
              sx={{
                color: "#4673D9",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "28px", sm: "36px", md: "48.5px" },
                fontWeight: 700,
                mr: 1,
                lineHeight: { xs: "36px", md: "1.2" },
              }}
            >
              Add-Ons
            </Typography>
            <Typography
              sx={{
                color: "#333336",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "28px", sm: "36px", md: "46.9px" },
                fontWeight: 700,
                lineHeight: { xs: "36px", md: "1.2" },
              }}
            >
              Features
            </Typography>
          </Stack>
        </Box>

        {/* Row 1: Original 3 Add-On Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "stretch", md: "center" },
            alignItems: { xs: "stretch", md: "stretch" },
            gap: { xs: 3, md: 3 },
            mt: { xs: 3, md: 4 },
            width: "100%",
            maxWidth: { xs: "100%", md: "1200px" },
            margin: { xs: "0", md: "0 auto" },
            pl: { xs: 0, md: "9%" },
            pr: { xs: 0, md: "9%" },
          }}
        >
          {/* Card 1: Body Summary */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src="/humanbodysummaryold.png"
                alt="Body Summary"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Body Summary
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Visual organ-level overview of affected areas
            </Typography>
            <Button
              onClick={() => handleOpenModal("/humanbodysummaryold.png", "Body Summary")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>

          {/* Card 2: Cover Page Customization */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.45, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src="/coverPageNew.png"
                alt="Cover Page Customization"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Cover Page Customization
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Branded layouts for professional credibility
            </Typography>
            <Button
              onClick={() => handleOpenModal("/coverPageNew.png", "Cover Page Customization")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>

          {/* Card 3: Test Recommender */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src={testRecommenderImage}
                alt="Test Recommender"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Test Recommender
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Smart follow-up recommendations powered by AI logic
            </Typography>
            <Button
              onClick={() => handleOpenModal(testRecommenderImage, "Test Recommender")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>
        </Box>

        {/* Row 2: New 4 Add-On Cards */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "stretch", md: "center" },
            alignItems: { xs: "stretch", md: "stretch" },
            gap: { xs: 3, md: 3 },
            width: "100%",
            maxWidth: { xs: "100%", md: "1200px" },
            margin: { xs: "24px 0 0 0", md: "24px auto 0 auto" },
            pl: { xs: 0, md: "9%" },
            pr: { xs: 0, md: "9%" },
          }}
        >
          {/* Card 4: Diet Page */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.75, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src={dietPageImage}
                alt="Diet Page"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Diet Page
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Personalized dietary recommendations based on report parameters
            </Typography>
            <Button
              onClick={() => handleOpenModal(dietPageImage, "Diet Page")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>

          {/* Card 5: Doctor Page */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src={doctorPageImage}
                alt="Doctor Page"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Doctor Page
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Clinical insights and direct notes for physician review
            </Typography>
            <Button
              onClick={() => handleOpenModal(doctorPageImage, "Doctor Page")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>

          {/* Card 6: Quick Health Summary */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1.05, duration: 0.6 }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src={quickHealthImage}
                alt="Quick Health Summary"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Quick Health Summary
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              At-a-glance overview of key health indicators
            </Typography>
            <Button
              onClick={() => handleOpenModal(quickHealthImage, "Quick Health Summary")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>
        </Box>

        {/* Row 3: Consolidated Page (single card centered) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "stretch", md: "flex-start" },
            alignItems: { xs: "stretch", md: "flex-start" },
            gap: { xs: 3, md: 3 },
            width: "100%",
            maxWidth: { xs: "100%", md: "1200px" },
            margin: { xs: "24px 0 0 0", md: "24px auto 0 auto" },
            pl: { xs: 0, md: "9%" },
            pr: { xs: 0, md: "9%" },
          }}
        >
          {/* Card 7: Consolidated Page */}
          <AddOnCard
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.6 }}
            sx={{
              // On desktop: limit to roughly one-third width (same as a card in a 3-col row)
              flex: { md: "0 0 calc(33.333% - 16px)" },
              maxWidth: { md: "calc(33.333% - 16px)" },
            }}
          >
            <ImageFrame
              sx={{
                mb: 2,
                height: IMAGE_HEIGHT,
              }}
            >
              <Box
                component="img"
                src={wellnezzPageImage}
                alt="Consolidated Page"
                sx={cardImageStyles}
              />
            </ImageFrame>
            <Typography sx={cardTitleStyles}>
              Consolidated Page
            </Typography>
            <Typography sx={cardDescriptionStyles}>
              Comprehensive grouping of all essential findings
            </Typography>
            <Button
              onClick={() => handleOpenModal(wellnezzPageImage, "Consolidated Page")}
              sx={previewButtonStyles}
            >
              <Eye size={16} />
              Preview
            </Button>
          </AddOnCard>
        </Box>

        {/* Image Preview Modal - New UI */}
        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="image-preview-modal"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(12px)",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            sx={{
              width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
              maxWidth: "700px",
              maxHeight: { xs: "85vh", sm: "88vh", md: "90vh" },
              background: "#FFFFFF",
              borderRadius: { xs: "20px", md: "24px" },
              outline: "none",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 80px rgba(0, 0, 0, 0.35)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: { xs: "16px 20px", md: "20px 28px" },
                borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
                flexShrink: 0,
              }}
            >
              <Typography
                id="image-preview-modal"
                sx={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: { xs: "20px", sm: "24px" },
                  fontWeight: 700,
                  color: "#1D47A4",
                  letterSpacing: "-0.02em",
                }}
              >
                {selectedTitle}
              </Typography>
              <IconButton
                onClick={handleCloseModal}
                sx={{
                  color: "#4674D9",
                  backgroundColor: "rgba(70, 116, 217, 0.08)",
                  width: { xs: "40px", md: "44px" },
                  height: { xs: "40px", md: "44px" },
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(70, 116, 217, 0.15)",
                    transform: "rotate(90deg)",
                  },
                }}
              >
                <X size={22} />
              </IconButton>
            </Box>

            {/* Image Body - top-aligned with full scrollable height */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                padding: { xs: "16px", md: "24px" },
                background: "#F5F7FA",
                display: "block",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#E8EDF5",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "linear-gradient(180deg, #4674D9 0%, #6E91EB 100%)",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "linear-gradient(180deg, #3563C8 0%, #5D80DA 100%)",
                  },
                },
              }}
            >
              {selectedImage && (
                <Box
                  sx={{
                    background: "#FFFFFF",
                    borderRadius: { xs: "12px", md: "16px" },
                    padding: { xs: "12px", md: "16px" },
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  }}
                >
                <Box
                  component="img"
                  src={selectedImage}
                  alt={selectedTitle}
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: { xs: "8px", md: "12px" },
                    display: "block",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
                  }}
                />
                </Box>
              )}
            </Box>
          </Box>
        </Modal>
      </Container>
    </AddOnsContainer>
  );
};

export default AddOnsSection;
